import { useEffect, useState } from 'react';

interface WateringProgressProps {
  status: 'ok' | 'soon' | 'overdue' | 'critical';
  daysUntil?: number;
  message?: string;
  wateringFrequencyDays?: number;
  compact?: boolean;
}

const fillClasses: Record<string, string> = {
  ok: 'progress-fill-ok',
  soon: 'progress-fill-soon',
  overdue: 'progress-fill-overdue',
  critical: 'progress-fill-critical',
};

const statusEmojis: Record<string, string> = {
  ok: '✅',
  soon: '💧',
  overdue: '⚠️',
  critical: '🆘',
};

// Inverted: full bar = just watered (good), empty bar = needs water (bad)
function getProgressPercent(status: string, daysUntil?: number, frequencyDays?: number): number {
  if (status === 'critical') return 3;
  if (status === 'overdue') return 5;
  if (daysUntil === undefined || !frequencyDays || frequencyDays <= 0) return 50;

  if (daysUntil <= 0) return 3;
  return Math.max(5, Math.min(100, (daysUntil / frequencyDays) * 100));
}

export default function WateringProgress({
  status,
  daysUntil,
  message,
  wateringFrequencyDays,
  compact = false,
}: WateringProgressProps) {
  const [width, setWidth] = useState(100);
  const percent = getProgressPercent(status, daysUntil, wateringFrequencyDays);

  useEffect(() => {
    const timer = setTimeout(() => setWidth(percent), 50);
    return () => clearTimeout(timer);
  }, [percent]);

  const isCritical = status === 'critical';

  return (
    <div className={compact ? '' : 'space-y-1.5'}>
      {/* Progress bar — full = just watered, empty = needs water */}
      <div
        className={`relative w-full rounded-full progress-track overflow-hidden ${
          compact ? 'h-[6px]' : 'h-[10px]'
        }`}
      >
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out ${fillClasses[status]} ${
            isCritical ? 'animate-pulse' : ''
          }`}
          style={{
            width: `${width}%`,
            ...(isCritical ? { boxShadow: '0 0 12px rgba(239, 68, 68, 0.5)' } : {}),
          }}
        />
      </div>

      {/* Label (full mode only) */}
      {!compact && message && (
        <div className="flex items-center gap-1.5 text-xs text-tg-hint">
          <span>{statusEmojis[status]}</span>
          <span>{message}</span>
        </div>
      )}
    </div>
  );
}
